/**
 * Shared placeholders used across pages.
 *
 *   <Empty />              inline "not set" placeholder for null fields
 *   <EmptyCard />          full empty-state block with optional CTA
 *   <PageLoader />         the rotating brand triangle, centred — THE preloader
 *   <LoadingBlock />       alias of PageLoader, kept so ~90 call sites keep working
 *   <ErrorMessage />       consistent inline error styling
 */
import type { ReactNode } from 'react';
import { classNames } from '@/lib/format';
import { ArollLoader } from '@/components/ArollLoader';

/** Inline placeholder for a null/missing scalar value (e.g. "Due: not set"). */
export function Empty({ label = 'Not set' }: { label?: string }) {
  return <span className="kg-empty-text">{label}</span>;
}

export function EmptyCard({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="kg-empty-card">
      <p className="font-serif text-xl text-kg-grey-700 dark:text-kg-grey-200">{title}</p>
      {description && (
        <p className="text-sm text-kg-grey-600 dark:text-kg-grey-300 mt-2 max-w-md mx-auto">{description}</p>
      )}
      {action && <div className="mt-6 flex justify-center">{action}</div>}
    </div>
  );
}

/** Animated grey bars; sized via `lines` prop. */
/**
 * THE preloader (Nick, 2026-07-28: "any time we have a preload can we
 * make sure it's that rotating triangle ... 3 times bigger ... centred
 * on the page").
 *
 * Was a stack of grey skeleton bars, which is a different visual
 * language from the A-Roll wait (the turning triangle) and made the two
 * halves of the app feel like different products. One mark now, in the
 * brand's own geometry.
 *
 * Centred on BOTH axes: `min-h` gives it a stage to sit in the middle of
 * rather than clinging to the top of whatever container it lands in.
 * `minHeight` is overridable for the handful of tight slots (a drawer, a
 * short panel) where a 60vh stage would be silly.
 */
export function PageLoader({
  className,
  /** Vertical stage to centre within. Defaults to most of the viewport. */
  minHeight = '60vh',
  /** Optional cycling status copy, e.g. ['Reading the tape', 'Picking takes']. */
  lines,
}: {
  className?: string;
  minHeight?: string;
  lines?: string[];
}) {
  return (
    <div
      className={classNames(
        'flex w-full flex-col items-center justify-center text-kg-grey-400 dark:text-white/50',
        className,
      )}
      style={{ minHeight }}
      aria-busy="true"
    >
      <ArollLoader lines={lines} />
    </div>
  );
}

/**
 * Kept as an alias so the ~90 existing `<LoadingBlock />` call sites all
 * convert at once. `lines` used to mean "how many skeleton bars"; it is
 * accepted and ignored rather than removed, so no call site breaks.
 */
export function LoadingBlock({
  className,
  minHeight,
}: {
  /** @deprecated skeleton-bar count, ignored since the triangle landed. */
  lines?: number;
  className?: string;
  minHeight?: string;
}) {
  return <PageLoader className={className} minHeight={minHeight} />;
}

export function ErrorMessage({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      // pu-alert: PictureUp's card and changes red (pictureup.css; inert in the CRM).
      className="pu-alert border-l-4 border-red-700 bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200 px-4 py-3 text-sm"
    >
      {children}
    </div>
  );
}
